import type {
  AdminGuide,
  GuideAudience,
  GuideCategory,
  Risk,
} from "@/guides/adminGuides.types";
import {
  VNextCard,
  VNextField,
  VNextInput,
  VNextPill,
} from "@/vnext/primitives";
import {
  getGuideLocationTone,
  getGuideRiskTone,
} from "@/vnext/routes/settings/guides/guidesRouteModel";

interface GuidesCatalogCardProps {
  audiences: GuideAudience[];
  categories: GuideCategory[];
  filteredGuides: AdminGuide[];
  onSearchChange: (value: string) => void;
  onSelectGuide: (guideId: string) => void;
  onToggleAudience: (audience: GuideAudience) => void;
  onToggleCategory: (category: GuideCategory) => void;
  onToggleRisk: (risk: Risk) => void;
  onToggleShowNotSupportedYet: () => void;
  resultCount: number;
  risks: Risk[];
  search: string;
  selectedAudiences: GuideAudience[];
  selectedCategories: GuideCategory[];
  selectedGuideId?: string;
  selectedRisks: Risk[];
  showNotSupportedYet: boolean;
  totalCount: number;
}

interface FilterGroupProps {
  items: string[];
  label: string;
  selectedValues: string[];
  onToggle: (value: string) => void;
}

const FilterGroup = ({
  items,
  label,
  onToggle,
  selectedValues,
}: FilterGroupProps) => (
  <div className="guides-route__filter-group">
    <p className="guides-route__filter-label">{label}</p>
    <div className="guides-route__filter-options">
      {items.map((item) => {
        const selected = selectedValues.includes(item);
        return (
          <button
            aria-pressed={selected}
            className={`guides-route__filter-chip${
              selected ? " is-selected" : ""
            }`}
            key={item}
            onClick={() => onToggle(item)}
            type="button"
          >
            {item}
          </button>
        );
      })}
    </div>
  </div>
);

export function GuidesCatalogCard({
  audiences,
  categories,
  filteredGuides,
  onSearchChange,
  onSelectGuide,
  onToggleAudience,
  onToggleCategory,
  onToggleRisk,
  onToggleShowNotSupportedYet,
  resultCount,
  risks,
  search,
  selectedAudiences,
  selectedCategories,
  selectedGuideId,
  selectedRisks,
  showNotSupportedYet,
  totalCount,
}: GuidesCatalogCardProps) {
  return (
    <VNextCard
      className="guides-route__catalog-card"
      subtitle="Search internal runbooks, filter by audience, and deep-link into a guide."
      title="Guide catalog"
    >
      <div className="guides-route__catalog-layout">
        <VNextField
          hint="Search title, keywords, troubleshooting notes, or steps."
          label="Search guides"
        >
          <VNextInput
            aria-label="Search guides"
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search guides"
            type="search"
            value={search}
          />
        </VNextField>

        <FilterGroup
          items={categories}
          label="Category"
          onToggle={(value) => onToggleCategory(value as GuideCategory)}
          selectedValues={selectedCategories}
        />

        <FilterGroup
          items={audiences}
          label="Audience"
          onToggle={(value) => onToggleAudience(value as GuideAudience)}
          selectedValues={selectedAudiences}
        />

        <FilterGroup
          items={risks}
          label="Risk"
          onToggle={(value) => onToggleRisk(value as Risk)}
          selectedValues={selectedRisks}
        />

        <label className="guides-route__checkbox">
          <input
            checked={showNotSupportedYet}
            onChange={onToggleShowNotSupportedYet}
            type="checkbox"
          />
          <span>Include not-supported-yet guides</span>
        </label>

        <div className="guides-route__catalog-summary">
          <p className="guides-route__catalog-count">
            {resultCount} guide{resultCount === 1 ? "" : "s"} shown
          </p>
          <p className="guides-route__catalog-total">
            {totalCount} total in the library
          </p>
        </div>

        <div className="guides-route__guide-list">
          {filteredGuides.length === 0 ? (
            <div className="guides-route__empty-list">
              <h3>No guides match those filters</h3>
              <p>Try removing a filter or searching for a broader term.</p>
            </div>
          ) : null}

          {filteredGuides.map((guide) => {
            const selected = guide.id === selectedGuideId;
            return (
              <button
                className={`guides-route__guide-list-item${
                  selected ? " is-selected" : ""
                }`}
                key={guide.id}
                onClick={() => onSelectGuide(guide.id)}
                type="button"
              >
                <div className="guides-route__guide-list-header">
                  <h3>{guide.title}</h3>
                  <div className="guides-route__guide-pills">
                    <VNextPill tone={getGuideRiskTone(guide.risk)}>
                      {guide.risk.toUpperCase()}
                    </VNextPill>
                    <VNextPill tone={getGuideLocationTone(guide.location)}>
                      {guide.location}
                    </VNextPill>
                  </div>
                </div>
                <p>{guide.summary}</p>
              </button>
            );
          })}
        </div>
      </div>
    </VNextCard>
  );
}
